<template>
  <div class="entry-area">
    <h3 class="form-ttl py-5">
      {{ $t('register-page.form-title', { split: '/' }) }}
    </h3>
    <div>
      <table border="1" cellpadding cellspacing="0" class="table-custom">
        <tbody>
          <!-- Avatar Field -->
          <FormField
            v-if="isShowBasicInformationField('img')"
            :title="$t('register-page.step-one.avatar', { split: '/' })"
            :required="false"
            :input-example="9"
            :input-examples="getInputExample"
          >
            <div class="button-upload-file">
              <UploadImage
                :imageAva="imageAva"
                :disabled="isFieldReadonly('avatar')"
                @getDataImage="getDataImage"
              />
            </div>
          </FormField>

          <!-- Name Field -->
          <FormField
            :title="$t('register-page.step-one.name', { split: '/' })"
            :required="isFieldRequired('name')"
            :input-example="0"
            :input-examples="getInputExample"
          >
            <VInputRegister
              ref="name"
              :v="$v"
              :validate="getFieldValidationRules('name')"
              name="name"
              @input="input"
            />
          </FormField>

          <!-- Name Kana Field -->
          <FormField
            v-if="isShowBasicInformationField('name_kana') && $i18n.locale === LANG_CODE_JAPANESE"
            :title="$t('register-page.step-one.name_kana', { split: '/' })"
            :required="isFieldRequired('name_kana')"
            :input-example="7"
            :input-examples="getInputExample"
          >
            <VInputRegister
              ref="name_kana"
              :v="$v"
              :validate="getFieldValidationRules('name_kana')"
              name="name_kana"
              @input="input"
            />
          </FormField>

          <!-- Gender Field -->
          <FormField
            :title="$t('register-page.step-one.gender', { split: '/' })"
            :required="isFieldRequired('gender')"
          >
            <GenderSelector v-model="gender" />
          </FormField>

          <!-- Birthday Field -->
          <FormField
            :title="$t('register-page.step-one.birthday', { split: '/' })"
            :required="isFieldRequired('birthday')"
          >
            <BirthdayPicker
              v-model="birthday"
              :formattedValue.sync="birthdayFormattedTextField"
              :validation-errors="{
                required: $v.birthday.$invalid && $v.birthday.$dirty && !$v.birthday.required,
                format: !$v.birthdayFormattedTextField.birthdayFormatted
              }"
            />
          </FormField>

          <!-- Email Fields -->
          <FormField
            :title="$t('register-page.step-one.mail-address', { split: '/' })"
            :required="isFieldRequired('mail_address')"
            :input-example="1"
            :input-examples="getInputExample"
          >
            <VInputRegister
              ref="mail_address"
              :v="$v"
              :validate="getFieldValidationRules('mail_address')"
              name="mail_address"
              @input="input"
            />
          </FormField>

          <FormField
            :title="$t('register-page.step-one.re-mail-address', { split: '/' })"
            :required="isFieldRequired('re_mail_address')"
            :input-example="2"
            :input-examples="getInputExample"
          >
            <VInputRegister
              ref="re_mail_address"
              :v="$v"
              :validate="getFieldValidationRules('re_mail_address')"
              name="re_mail_address"
              @input="input"
            />
          </FormField>

          <!-- Phone Field -->
          <FormField
            :title="$t('register-page.step-one.phone-number', { split: '/' })"
            :required="isFieldRequired('phone_number')"
            :input-example="3"
            :input-examples="getInputExample"
          >
            <VInputRegister
              ref="phone_number"
              :v="$v"
              :validate="getFieldValidationRules('phone_number')"
              name="phone_number"
              @input="input"
            />
          </FormField>

          <!-- Address Fields -->
          <FormField
            :title="$t('register-page.step-one.address1', { split: '/' })"
            :required="isFieldRequired('area_id')"
            :input-example="4"
            :input-examples="getInputExample"
          >
            <SelectField
              v-model="area_id"
              name="area_id"
              :readonly="isFieldReadonly('area_id')"
              :validation-error="$v.area_id.$invalid && $v.area_id.$dirty && !$v.area_id.required"
            >
              <template #options>
                <option
                  v-for="item in m_area"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.area_name }}
                </option>
              </template>
            </SelectField>
          </FormField>

          <FormField
            :title="$t('register-page.step-one.address2', { split: '/' })"
            :required="isFieldRequired('address')"
            :input-example="5"
            :input-examples="getInputExample"
          >
            <VInputRegister
              ref="address"
              :v="$v"
              :validate="getFieldValidationRules('address')"
              name="address"
              @input="input"
            />
          </FormField>

          <!-- Hometown Field -->
          <FormField
            :title="$t('register-page.step-one.hometown', { split: '/' })"
            :required="isFieldRequired('hometown_id')"
            :input-example="6"
            :input-examples="getInputExample"
          >
            <SelectField
              v-model="hometown_id"
              name="hometown_id"
              :readonly="isFieldReadonly('hometown_id')"
              :validation-error="$v.hometown_id.$invalid && $v.hometown_id.$dirty && !$v.hometown_id.required"
            >
              <template #options>
                <option
                  v-for="item in m_hometown"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.hometown_name }}
                </option>
              </template>
            </SelectField>
          </FormField>

          <!-- Facebook Field -->
          <FormField
            v-if="isShowBasicInformationField('facebook')"
            :title="$t('register-page.step-one.facebook')"
            :required="isFieldRequired('facebook')"
            :input-example="7"
            :input-examples="getInputExample"
          >
            <VInputRegister
              ref="facebook"
              :v="$v"
              :validate="getFieldValidationRules('facebook')"
              name="facebook"
              @input="input"
            />
          </FormField>

          <!-- Timezone Field -->
          <FormField
            v-if="isShowBasicInformationField('time_zone')"
            :title="$t('register-page.step-one.timezone', { split: '/' })"
            :required="isFieldRequired('time_zone')"
            :input-example="8"
            :input-examples="getInputExample"
          >
            <SelectField
              v-model="time_zone"
              name="time_zone"
              :readonly="isFieldReadonly('time_zone')"
              :validation-error="$v.time_zone.$invalid && $v.time_zone.$dirty && !$v.time_zone.required"
              :has-error="$v.time_zone.$invalid && $v.time_zone.$dirty"
            >
              <template #options>
                <template v-for="item in c_time_zone">
                  <option
                    v-if="checkTimeZone(item.id)"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name_convert }}
                  </option>
                </template>
              </template>
            </SelectField>
          </FormField>
        </tbody>
      </table>

      <div class="widget-button py-5 text-center">
        <!-- <button class="btn-next" v-on:click="nextStepTwo" :loading="btnLoading">
       {{ $t('register-page.btn.btn-next', { split: '/' }) }}
       </button> -->

        <v-btn
          :loading="btnLoading"
          class="btn-next"
          color="#e8320e"
          depressed
          @click="nextStepTwo"
        >
          {{ $t('register-page.btn.btn-next', { split: '/' }) }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mdiCalendar } from '@mdi/js';
import {
  email,
  helpers,
  maxLength,
  required,
  sameAs
} from 'vuelidate/lib/validators';
import UploadImage from '@/components/UploadImage.vue';
import FormField from '@/components/entry/FormField.vue';
import GenderSelector from '@/components/entry/GenderSelector.vue';
import BirthdayPicker from '@/components/entry/BirthdayPicker.vue';
import SelectField from '@/components/entry/SelectField.vue';
import VInputRegister from '@/components/entry/VInputRegister';
import {
  EVENT_DEFAULT,
  FEMALE,
  HINT_TEXT_DATE_JAPANESE,
  HINT_TEXT_DATE_VIETNAMESE,
  LANG_CODE_JAPANESE,
  LANG_JAPANESE,
  LANG_VIETNAMESE,
  MALE,
  OTHER,
  TIME_ZONE_JP,
  TIME_ZONE_VN
} from '@/common/constants';
import { dateFormated } from '@/validators/date';
import axios from 'axios';
import url from '@/constants/api-url.js';
import { http } from '@/common/api';

export default {
  name: 'StepOne',
  components: {
    UploadImage,
    FormField,
    GenderSelector,
    BirthdayPicker,
    SelectField,
    VInputRegister
  },
  props: ['urlRegisterCode'],
  data: (vm) => {
    return {
      EVENT_DEFAULT,
      image_name: '',
      imageAva: '',
      name: '',
      name_kana: '',
      gender: MALE,
      mail_address: '',
      re_mail_address: '',
      phone_number: '',
      area_id: '',
      hometown_id: '',
      facebook: '',
      time_zone: '',
      address: '',
      calendarIcon: mdiCalendar,
      modal: false,
      date: new Date().toISOString().substr(0, 10),

      dateFormatted: vm.formatDate(new Date().toISOString().substr(0, 10)),

      // Set birthday to today minus 18 years
      birthday: (() => {
        const today = new Date();
        today.setFullYear(today.getFullYear() - 18);
        return today.toISOString().substr(0, 10);
      })(),
      modal_birthday: false,
      birthdayFormattedTextField: vm.formatDate(
        new Date().toISOString().substr(0, 10)
      ),

      checkEmailExisted: true,
      btnLoading: false
    };
  },

  validations() {
    const createDynamicValidations = () => {
      let baseValidations = {};
      if (this.m_template.participant_basic_info_type === this.EVENT_DEFAULT) {
        baseValidations = {
          name: {
            required,
            maxLength: maxLength(100)
          },
          birthday: {
            required
          },
          birthdayFormattedTextField: {
            birthdayFormatted: dateFormated('birthdayFormattedTextField')
          },
          mail_address: {
            required,
            email,
            maxLength: maxLength(50),
            mailExisted: function () {
              return this.checkEmailExisted;
            }
          },
          re_mail_address: {
            required,
            email,
            sameAs: sameAs('mail_address')
          },
          phone_number: {
            required,
            maxLength: maxLength(20),
            helpers: helpers.regex('', /^[ 0-9-+()]+$/)
          },
          address: {
            required,
            maxLength: maxLength(100)
          },
          area_id: {
            required
          },
          hometown_id: {
            required
          },
          facebook: {
            maxLength: maxLength(100)
          },
          time_zone: {
            required
          }
        };
      } else if (this.$i18n.locale === LANG_CODE_JAPANESE) {
        baseValidations = {
          name: {
            required,
            maxLength: maxLength(100)
          },
          name_kana: {
            required,
            maxLength: maxLength(100)
          },
          birthday: {
            required
          },
          birthdayFormattedTextField: {
            birthdayFormatted: dateFormated('birthdayFormattedTextField')
          },
          mail_address: {
            required,
            email,
            maxLength: maxLength(50),
            mailExisted: function () {
              return this.checkEmailExisted;
            }
          },
          re_mail_address: {
            required,
            email,
            sameAs: sameAs('mail_address')
          },
          phone_number: {
            maxLength: maxLength(20),
            helpers: helpers.regex('', /^[ 0-9-+()]+$/)
          },
          address: {
            required,
            maxLength: maxLength(100)
          },
          area_id: {
            required
          },
          hometown_id: {
            required
          }
        };
      } else {
        baseValidations = {
          name: {
            required,
            maxLength: maxLength(100)
          },
          birthday: {
            required
          },
          birthdayFormattedTextField: {
            birthdayFormatted: dateFormated('birthdayFormattedTextField')
          },
          mail_address: {
            required,
            email,
            maxLength: maxLength(50),
            mailExisted: function () {
              return this.checkEmailExisted;
            }
          },
          re_mail_address: {
            required,
            email,
            sameAs: sameAs('mail_address')
          },
          phone_number: {
            maxLength: maxLength(20),
            helpers: helpers.regex('', /^[ 0-9-+()]+$/)
          },
          address: {
            required,
            maxLength: maxLength(100)
          },
          area_id: {
            required
          },
          hometown_id: {
            required
          }
        };
      }

      // Apply form modifications only if they exist
      Object.keys(baseValidations).forEach((fieldName) => {
        const modification = this.m_event.modify_fields.find(
          (mod) => mod.name === fieldName
        );
        if (modification && modification.hasOwnProperty('required')) {
          if (modification.required === false) {
            delete baseValidations[fieldName].required;
          } else if (
            modification.required === true &&
            !baseValidations[fieldName].required
          ) {
            // Add required validation if not already present
            baseValidations[fieldName].required = required;
          }
        }
        // Handle type property modifications - convert numeric fields to text
        if (modification &&
            modification.type === 'text' &&
            baseValidations[fieldName].hasOwnProperty('numeric')) {
          // Remove numeric and between validators for text type fields
          delete baseValidations[fieldName].numeric;
          delete baseValidations[fieldName].between;
        }
      });

      return baseValidations;
    };

    return createDynamicValidations();
  },
  watch: {
    birthday(val, oldVal) {
      if (val !== oldVal) {
        this.birthdayFormattedTextField = this.formatDateString(val, this.$i18n.locale === LANG_CODE_JAPANESE);
      }
    },

    '$i18n.locale'(val, oldVal) {
      if (val !== oldVal) {
        this.birthdayFormattedTextField = this.formatDateString(this.birthday, val === LANG_CODE_JAPANESE);
        this.time_zone = val === LANG_CODE_JAPANESE ? TIME_ZONE_JP : TIME_ZONE_VN;
      }
    },

    mail_address(val, oldVal) {
      if (val !== oldVal) {
        this.checkEmailExisted = true;
      }
    }
  },

  methods: {
    // ====================
    // INPUT HANDLING METHODS
    // ====================
    input(data) {
      const { name, value } = data;
      if (name !== null) {
        this[name] = value;
      }
    },

    getDataImage: async function (data) {
      this.imageAva = data;
      this.btnLoading = true;
      const s3Url = await this.uploadImageS3(data);
      setTimeout(() => {
        this.imageAva = s3Url !== undefined && s3Url.length ? s3Url : data;
        this.btnLoading = false;
      }, 1200);
    },

    // ====================
    // FIELD CONFIGURATION METHODS
    // ====================
    isFieldRequired(fieldName) {
      const modification = this.m_event.modify_fields.find(
        (mod) => mod.name === fieldName
      );
      if (modification && modification.hasOwnProperty('required')) {
        return modification.required;
      }
      const defaultRequired = [
        'name', 'name_en', 'gender', 'birthday', 'mail_address',
        're_mail_address', 'address', 'hometown_id', 'area_id', 'time_zone'
      ];
      if (this.m_template.participant_basic_info_type === this.EVENT_DEFAULT) {
        defaultRequired.push('phone_number');
      }
      return defaultRequired.includes(fieldName);
    },

    isFieldReadonly(fieldName, defaultValue = false) {
      const modification = this.m_event.modify_fields.find(
        (mod) => mod.name === fieldName
      );
      return modification ? modification.readonly : defaultValue;
    },

    getFieldDefaultValue(fieldName) {
      const modification = this.m_event.modify_fields.find(
        (mod) => mod.name === fieldName
      );
      return modification && modification.default_value
        ? modification.default_value
        : '';
    },

    getFieldValidationRules(fieldName) {
      const rules = [];
      if (this.isFieldRequired(fieldName)) {
        rules.push({ type: 'required', pathTextLang: 'validate.required' });
      }

      const fieldRules = {
        name: [{ type: 'maxLength', pathTextLang: 'validate.out-of-range' }],
        name_kana: [{ type: 'maxLength', pathTextLang: 'validate.out-of-range' }],
        mail_address: [
          { type: 'email', pathTextLang: 'validate.email-invalid' },
          { type: 'mailExisted', pathTextLang: 'validate.email-duplicated' },
          { type: 'maxLength', pathTextLang: 'validate.out-of-range' }
        ],
        re_mail_address: [
          { type: 'email', pathTextLang: 'validate.email-invalid' },
          { type: 'sameAsEmail', pathTextLang: 'validate.email-not-match' }
        ],
        phone_number: [
          { type: 'maxLength', pathTextLang: 'validate.out-of-range' },
          { type: 'helpers', pathTextLang: 'validate.phone-format' }
        ],
        address: [{ type: 'maxLength', pathTextLang: 'validate.out-of-range' }]
      };

      return rules.concat(fieldRules[fieldName] || []);
    },

    preventChangeIfReadonly(event, fieldName) {
      if (this.isFieldReadonly(fieldName)) {
        event.preventDefault();
      }
    },

    // ====================
    // DATE HANDLING METHODS
    // ====================
    handleBirthdayFromTextField() {
      let string = '';
      const datetime = this.birthdayFormattedTextField;

      if (this.checkDateFormate(datetime)) {
        if (this.$i18n.locale === LANG_CODE_JAPANESE) {
          const [year, month, day] = datetime.split('/');
          string = year + '-' + month + '-' + day;
        } else {
          const [day, month, year] = datetime.split('/');
          string = year + '-' + month + '-' + day;
        }
        this.birthday = string;
      }
    },

    formatDateString(datetime, isJapanese) {
      const [year, month, day] = datetime.split('-');
      return isJapanese
        ? `${year}/${month}/${day}`
        : `${day}/${month}/${year}`;
    },

    // ====================
    // VALIDATION METHODS
    // ====================
    inputCheck(name) {
      if (this.$v[name].$invalid && this.$v[name].$dirty) {
        this.$refs[name].$refs[name].focus();
      }
    },

    validateForm() {
      this.$v.$touch();
      const fieldsToCheck = ['address', 'hometown_id', 'phone_number', 're_mail_address', 'mail_address', 'name'];
      fieldsToCheck.forEach(field => this.inputCheck(field));

      if (
        this.m_template.participant_basic_info_type !== this.EVENT_DEFAULT &&
        this.$i18n.locale === LANG_CODE_JAPANESE
      ) {
        this.inputCheck('name_kana');
      }
    },

    // ====================
    // FORM SUBMISSION METHODS
    // ====================
    nextStep() {
      if (!this.$v.$error && this.checkEmailExisted) {
        const {
          name, name_kana, gender, birthday, mail_address, re_mail_address,
          phone_number, address, area_id, hometown_id, facebook, time_zone
        } = this;

        const { event_id, contract_id, event_code } = this.$store.state.checkEvent;
        const lang = this.$i18n.locale === LANG_CODE_JAPANESE ? LANG_JAPANESE : LANG_VIETNAMESE;

        const data = {
          img: this.imageAva,
          event_id,
          contract_code: contract_id,
          event_code,
          lang,
          name: this.convertStr(name),
          name_kana: this.convertStr(name_kana),
          gender,
          birthday,
          mail_address,
          re_mail_address,
          phone_number: this.convertStr(phone_number),
          address: this.convertStr(address),
          area_id,
          hometown_id,
          facebook: this.convertStr(facebook),
          time_zone
        };

        this.$store.dispatch('storageStepOne', { data });
        this.$router.replace({
          name: 'register-applicant',
          query: {
            event_day_register_url_code: this.urlRegisterCode,
            step: 2
          }
        });
      }
    },

    nextStepTwo() {
      this.validateForm();
      this.btnLoading = true;

      const { event_id } = this.$store.state.checkEvent;
      const dataCheckEmail = { event_id, mail_address: this.mail_address };

      this.$store.dispatch('checkEmailExisted', dataCheckEmail).then((res) => {
        this.checkEmailExisted = res === 0;
        this.nextStep();
        this.btnLoading = false;
      });
    },

    // ====================
    // UTILITY METHODS
    // ====================
    async uploadImageS3(data) {
      if (!this.image_name) this.image_name = this.generateString(40);
      const state = this.$store.state;
      const { event_id, contract_id, event_code } = state.checkEvent;
      const signData = {
        file_name: this.image_name + '.png',
        contract_id,
        event_id,
        event_code
      };

      try {
        const res = await http(state, false).post(`${url.SIGN_S3}`, signData);
        const signUrl = res.data.pre_signed;
        const s3Url = signUrl.substring(0, signUrl.indexOf('?'));
        const axiosResponse = await axios.put(
          signUrl,
          this.convertDataUrlToBlob(data),
          {
            headers: {
              'Content-Type': 'image/png',
              'Content-Disposition': `attachment; filename=${signData.file_name}`,
              'Access-Control-Allow-Origin': '*'
            },
            withCredentials: false
          }
        );

        if (axiosResponse.status === 200 || axiosResponse.status === 201) {
          return s3Url;
        }
        return null;
      } catch (err) {
        console.log(err);
        return null;
      }
    },

    checkTimeZone(id) {
      const listId = this.m_template.timezone
        .replace(/[ \[\]]/g, '')
        .split(',');
      return listId.indexOf(id + '') > -1;
    },

    checkDateFormate(dateString) {
      const regex = this.$i18n.locale === LANG_CODE_JAPANESE
        ? /^\d{4}\/\d{1,2}\/\d{1,2}$/
        : /^\d{1,2}\/\d{1,2}\/\d{4}$/;
      return regex.test(dateString);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return this.formatDateString(dateString, this.$i18n.locale === LANG_CODE_JAPANESE);
    },

    generateString(length) {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      return result;
    },

    convertStr(str) {
      return str ? str.trim() : '';
    },

    convertDataUrlToBlob(dataUrl) {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    }
  },

  computed: {
    ...mapGetters({
      m_template: 'getMTemplate',
      m_event: 'getEventData'
    }),

    getHint() {
      if (this.$i18n.locale === LANG_CODE_JAPANESE) {
        return HINT_TEXT_DATE_JAPANESE;
      } else {
        return HINT_TEXT_DATE_VIETNAMESE;
      }
    },

    getInputExample() {
      return this.m_input_example.filter((el) => el.screen_id === 1);
    },

    // Constants for template access
    LANG_CODE_JAPANESE() {
      return LANG_CODE_JAPANESE;
    }
  },

  created() {
    // assign value constant
    this.MALE = MALE;
    this.FEMALE = FEMALE;
    this.OTHER = OTHER;
    this.time_zone = this.m_event.time_zone;

    // if (this.$i18n.locale === LANG_CODE_JAPANESE) {
    //   return (this.time_zone = TIME_ZONE_JP);
    // } else {
    //   return (this.time_zone = TIME_ZONE_VN);
    // }
  },

  mounted() {
    // Load stored data if exists
    const registerData =
      localStorage.getItem('registerData') !== null
        ? JSON.parse(localStorage.getItem('registerData'))
        : null;

    if (registerData !== null && registerData.stepOne) {
      const stepOne = registerData.stepOne;
      Object.keys(stepOne).forEach((key) => {
        if (this.hasOwnProperty(key)) {
          this[key] = stepOne[key];
        }
      });
    } else {
      // Load default values from m_event.modify_fields
      if (this.m_event && this.m_event.modify_fields) {
        this.m_event.modify_fields.forEach((field) => {
          if (field.default_value && this.hasOwnProperty(field.name)) {
            this[field.name] = field.default_value;
          }
        });
      }
    }
  }
};
</script>

<style>
.input-custom {
  height: 55px;
  max-width: calc(90%);
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .table-custom .td-left {
    width: 35% !important;
  }
}
.input-example-style {
  margin-top: 1rem;
  white-space: pre-wrap;
}
</style>
